import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginResponse, SessionInfo } from '@oms/contracts';
import { api, isApiError } from './client';
import type { BrandingResponse, Ok } from './types';

export const sessionKey = ['session'] as const;

/** Current session, or null when signed out. Never throws for 401. */
export async function fetchSession(): Promise<SessionInfo | null> {
  try {
    return await api.get<SessionInfo>('/auth/session', undefined, { allowUnauthenticated: true });
  } catch (e) {
    if (isApiError(e) && e.status === 401) return null;
    throw e;
  }
}

export function useSession() {
  return useQuery({ queryKey: sessionKey, queryFn: fetchSession, staleTime: 60_000 });
}

export function useBranding(host: string, slug: string | null) {
  return useQuery({
    queryKey: ['branding', host, slug ?? ''],
    queryFn: () => api.get<BrandingResponse>('/public/tenant-branding', { host, slug }, { allowUnauthenticated: true }),
    staleTime: 5 * 60_000,
  });
}

export interface LoginInput {
  email: string;
  password: string;
  tenantSlug?: string | undefined;
}

/**
 * Signs in. The API sets the HttpOnly `oms_session` cookie; the bearer token in the response body
 * is meant for mobile/API clients and is deliberately dropped here (never stored or logged).
 */
export function useLogin(scope: 'tenant' | 'platform') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginInput): Promise<SessionInfo> => {
      const res = await api.post<LoginResponse>(scope === 'platform' ? '/auth/platform-login' : '/auth/login', input, { allowUnauthenticated: true });
      return res.session;
    },
    onSuccess: (session) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'branding' });
      qc.setQueryData(sessionKey, session);
    },
  });
}

export function useSelectTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => api.post<SessionInfo>('/auth/select-tenant', { tenantId }),
    onSuccess: (session) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'branding' });
      qc.setQueryData(sessionKey, session);
    },
  });
}

function useSignOutMutation(path: '/auth/logout' | '/auth/logout-all') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Ok>(path, {}, { allowUnauthenticated: true }),
    onSettled: () => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'branding' });
      qc.setQueryData(sessionKey, null);
    },
  });
}
export const useLogout = () => useSignOutMutation('/auth/logout');
export const useLogoutAll = () => useSignOutMutation('/auth/logout-all');

export function useChangePassword() {
  return useMutation({ mutationFn: (input: { currentPassword: string; newPassword: string }) => api.post<Ok>('/auth/change-password', input) });
}

export function usePasswordResetRequest() {
  return useMutation({
    mutationFn: (input: { email: string }) => api.post<{ ok: true; message: string }>('/auth/password-reset/request', input, { allowUnauthenticated: true }),
  });
}

export function usePasswordResetConfirm() {
  return useMutation({
    mutationFn: (input: { token: string; newPassword: string }) => api.post<Ok>('/auth/password-reset/confirm', input, { allowUnauthenticated: true }),
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (input: { token: string; fullName?: string | undefined; password: string }) => api.post<Ok>('/auth/invitations/accept', input, { allowUnauthenticated: true }),
  });
}
